import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection, query, where, getDocs, orderBy, limit, updateDoc, doc, serverTimestamp
} from 'firebase/firestore';

function AssociationDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [stats, setStats] = useState({
    totalTournaments: 0,
    activeTournaments: 0,
    registrationOpen: 0,
    completedTournaments: 0,
    totalParticipants: 0,
    totalPrizePool: 0
  });
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [pendingApplications, setPendingApplications] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  // useEffect는 user?.id만 의존성에 둠
  useEffect(() => {
    console.log("useEffect 진입", user);
    if (!user) {
      console.log("user 없음, navigate");
      navigate('/');
      return;
    }
    if (user.userType !== 'association') {
      console.log("userType이 association이 아님, navigate");
      navigate('/');
      return;
    }
    console.log("loadDashboardData 호출");
    loadDashboardData();
    // eslint-disable-next-line
  }, [user?.id]);

  // 대시보드 데이터 로드
  const loadDashboardData = async () => {
    console.log("loadDashboardData 시작");
    setLoading(true);
    setMsg('');
    
    try {
      await Promise.all([
        loadTournamentStats(),
        loadRecentTournaments(),
        loadPendingApplications(),
        loadRecentActivities()
      ]);
      console.log("loadDashboardData 데이터 로드 완료");
    } catch (error) {
      console.error("대시보드 데이터 로딩 실패:", error);
      setMsg("데이터를 불러오는 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
      console.log("loadDashboardData 끝, setLoading(false)");
    }
  };

  // 대회 통계 로드
  const loadTournamentStats = async () => {
    console.log("loadTournamentStats 시작");
    if (!user) return;
    
    try {
      const tournamentsQuery = query(
        collection(db, 'tournaments'),
        where('organizerId', '==', user.id)
      );
      const tournamentsSnap = await getDocs(tournamentsQuery);
      
      let totalTournaments = 0;
      let activeTournaments = 0;
      let registrationOpen = 0;
      let completedTournaments = 0;
      let totalParticipants = 0;
      let totalPrizePool = 0;

      tournamentsSnap.docs.forEach(doc => {
        const tournament = doc.data();
        totalTournaments++;
        
        switch (tournament.status) {
          case 'registration':
            registrationOpen++;
            break;
          case 'active':
          case 'in_progress':
            activeTournaments++;
            break;
          case 'completed':
            completedTournaments++;
            break;
        }
        
        totalParticipants += (tournament.participants?.length || 0);
        totalPrizePool += (tournament.prizePool || 0);
      });

      setStats({
        totalTournaments,
        activeTournaments,
        registrationOpen,
        completedTournaments,
        totalParticipants,
        totalPrizePool
      });
    } catch (error) {
      console.error("통계 로드 실패:", error);
      // 에러가 발생해도 기본값으로 설정
      setStats({
        totalTournaments: 0,
        activeTournaments: 0,
        registrationOpen: 0,
        completedTournaments: 0,
        totalParticipants: 0,
        totalPrizePool: 0
      });
    } finally {
      console.log("loadTournamentStats 끝");
    }
  };

  // 최근 대회 로드
  const loadRecentTournaments = async () => {
    console.log("loadRecentTournaments 시작");
    if (!user) return;
    
    try {
      // orderBy 대신 클라이언트 정렬 사용
      const recentQuery = query(
        collection(db, 'tournaments'),
        where('organizerId', '==', user.id)
      );
      const recentSnap = await getDocs(recentQuery);
      
      const tournaments = recentSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // 최신순으로 정렬
      tournaments.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      });
      
      setRecentTournaments(tournaments.slice(0, 5));
    } catch (error) {
      console.error("최근 대회 로드 실패:", error);
      setRecentTournaments([]);
    } finally {
      console.log("loadRecentTournaments 끝");
    }
  };

  // 대기 중인 참가 신청 로드
  const loadPendingApplications = async () => {
    console.log("loadPendingApplications 시작");
    if (!user) return;
    
    try {
      // orderBy 대신 클라이언트 정렬 사용
      const applicationsQuery = query(
        collection(db, 'tournamentApplications'),
        where('organizerId', '==', user.id),
        where('status', '==', 'pending')
      );
      const applicationsSnap = await getDocs(applicationsQuery);
      
      const applicationsData = [];
      for (const appDoc of applicationsSnap.docs) {
        const appData = appDoc.data();
        
        // 신청자 정보 가져오기
        const userQuery = query(
          collection(db, 'users'),
          where('id', '==', appData.userId)
        );
        const userSnap = await getDocs(userQuery);
        
        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();
          applicationsData.push({
            id: appDoc.id,
            ...appData,
            userNickname: userData.nickname || userData.id,
            userMMR: userData.mmr || 0
          });
        }
      }

      // 최신순으로 정렬
      applicationsData.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      });
      
      setPendingApplications(applicationsData.slice(0, 10));
    } catch (error) {
      console.error("대기 중인 신청 로드 실패:", error);
      setPendingApplications([]);
    } finally {
      console.log("loadPendingApplications 끝");
    }
  };

  // 최근 활동 로드
  const loadRecentActivities = async () => {
    console.log("loadRecentActivities 시작");
    if (!user) return;
    
    try {
      // orderBy 대신 클라이언트 정렬 사용
      const activitiesQuery = query(
        collection(db, 'associationActivities'),
        where('associationId', '==', user.id)
      );
      const activitiesSnap = await getDocs(activitiesQuery);
      
      const activities = activitiesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // 최신순으로 정렬
      activities.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      });

      setRecentActivities(activities.slice(0, 10));
    } catch (error) {
      console.error("최근 활동 로드 실패:", error);
      setRecentActivities([]);
    } finally {
      console.log("loadRecentActivities 끝");
    }
  };

  // 참가 신청 승인/거절
  const handleApplicationDecision = async (applicationId, decision) => {
    try {
      await updateDoc(doc(db, 'tournamentApplications', applicationId), {
        status: decision,
        decidedAt: serverTimestamp(),
        decidedBy: user.id
      });

      // 대기 목록에서 제거
      setPendingApplications(prev => 
        prev.filter(app => app.id !== applicationId)
      );

      setMsg(`참가 신청이 ${decision === 'approved' ? '승인' : '거절'}되었습니다.`);
    } catch (error) {
      console.error("신청 처리 실패:", error);
      setMsg("신청 처리에 실패했습니다.");
    }
  };

  // 상태 배지 스타일
  const getStatusBadge = (status) => {
    const statusConfig = {
      'registration': { label: '모집중', color: '#10b981', bg: '#d1fae5' },
      'active': { label: '진행중', color: '#3b82f6', bg: '#dbeafe' },
      'in_progress': { label: '진행중', color: '#3b82f6', bg: '#dbeafe' },
      'completed': { label: '완료', color: '#6b7280', bg: '#f3f4f6' },
      'cancelled': { label: '취소', color: '#ef4444', bg: '#fee2e2' }
    };
    const config = statusConfig[status] || statusConfig.registration;
    
    return (
      <span style={{
        background: config.bg,
        color: config.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {config.label}
      </span>
    );
  };

  // 날짜 포맷팅
  const formatDate = (date) => {
    if (!date) return '-';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('ko-KR');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 24,
          padding: 48,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}>
          <div style={{
            width: 60,
            height: 60,
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 24px auto'
          }}></div>
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#1e293b',
            marginBottom: 12
          }}>
            📊 대시보드 로딩중...
          </h2>
          <p style={{
            fontSize: 16,
            color: '#64748b'
          }}>
            협회 데이터를 불러오고 있습니다
          </p>
        </div>
      </div>
    );
  }

  // 데이터가 모두 비어있는지 체크
  const isEmptyDashboard =
    stats.totalTournaments === 0 &&
    recentTournaments.length === 0 &&
    pendingApplications.length === 0 &&
    recentActivities.length === 0;

  if (!loading && !msg && isEmptyDashboard) {
    return (
      <div style={{
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#64748b',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          fontSize: 64,
          marginBottom: 24
        }}>📭</div>
        <h2 style={{ fontWeight: 700, fontSize: 24, marginBottom: 12 }}>
          대시보드 데이터가 없습니다
        </h2>
        <p style={{ fontSize: 16, textAlign: 'center' }}>
          아직 등록된 대회, 신청, 활동 내역이 없습니다.<br />
          새로운 대회를 생성해보세요!
        </p>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '40px auto',
      padding: '0 20px'
    }}>
      {/* CSS 애니메이션 */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* 메시지 표시 */}
      {msg && (
        <div style={{
          background: msg.includes('실패') || msg.includes('오류') ? '#fee2e2' : '#d1fae5',
          color: msg.includes('실패') || msg.includes('오류') ? '#dc2626' : '#065f46',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: '14px'
        }}>
          {msg}
        </div>
      )}

      {/* 헤더 */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '24px',
        padding: '40px 32px',
        color: '#fff',
        marginBottom: '32px',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          opacity: '0.5'
        }} />
        
        <div style={{ position: 'relative', zIndex: '1' }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '900',
            margin: '0',
            marginBottom: '8px',
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            🏛️ {user.associationName || '협회'} 대시보드
          </h1>
          <p style={{
            fontSize: '18px',
            margin: '0',
            opacity: '0.9'
          }}>
            협회 운영 현황을 한눈에 확인하고 관리하세요
          </p>
        </div>
      </div>

      {/* 통계 카드들 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {[
          { 
            title: '총 대회 수', 
            value: stats.totalTournaments, 
            icon: '🏆', 
            color: '#3b82f6',
            bg: '#eff6ff'
          },
          { 
            title: '모집중 대회', 
            value: stats.registrationOpen, 
            icon: '📝', 
            color: '#10b981',
            bg: '#f0fdf4'
          },
          { 
            title: '진행중 대회', 
            value: stats.activeTournaments, 
            icon: '⚡', 
            color: '#f59e0b',
            bg: '#fffbeb'
          },
          { 
            title: '완료 대회', 
            value: stats.completedTournaments, 
            icon: '✅', 
            color: '#8b5cf6',
            bg: '#f5f3ff'
          },
          { 
            title: '총 참가자', 
            value: stats.totalParticipants, 
            icon: '👥', 
            color: '#ef4444',
            bg: '#fef2f2'
          },
          { 
            title: '총 상금', 
            value: `${stats.totalPrizePool.toLocaleString()}원`, 
            icon: '💰', 
            color: '#06b6d4',
            bg: '#f0fdfa'
          }
        ].map((stat, index) => (
          <div
            key={index}
            style={{
              background: stat.bg,
              borderRadius: '16px',
              padding: '24px',
              border: `1px solid ${stat.color}20`,
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '24px'
              }}>
                {stat.icon}
              </div>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: stat.color,
                margin: '0'
              }}>
                {stat.title}
              </h3>
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: stat.color
            }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* 빠른 작업 */}
      <div style={{
        background: '#fff',
        borderRadius: '20px',
        padding: '32px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f1f5f9',
        marginBottom: '32px'
      }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '700',
          marginBottom: '20px',
          color: '#1f2937'
        }}>
          🚀 빠른 작업
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          {[
            { 
              title: '새 대회 생성', 
              icon: '➕', 
              path: '/association/create-tournament',
              color: '#10b981',
              desc: '새로운 볼링 대회를 개최하세요'
            },
            { 
              title: '대회 관리', 
              icon: '⚙️', 
              path: '/tournaments',
              color: '#3b82f6',
              desc: '진행 중인 대회를 관리하세요'
            },
            { 
              title: '협회 프로필', 
              icon: '🏢', 
              path: '/association/profile',
              color: '#8b5cf6',
              desc: '협회 정보를 수정하세요'
            },
            { 
              title: '통계 보기', 
              icon: '📊', 
              path: '/association/analytics',
              color: '#f59e0b',
              desc: '상세한 운영 통계를 확인하세요'
            }
          ].map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              style={{
                background: `${action.color}10`,
                border: `2px solid ${action.color}30`,
                borderRadius: '16px',
                padding: '20px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'left'
              }}
              onMouseOver={e => {
                e.target.style.background = `${action.color}20`;
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 8px 25px ${action.color}40`;
              }}
              onMouseOut={e => {
                e.target.style.background = `${action.color}10`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <div style={{ 
                fontSize: '24px', 
                marginBottom: '8px' 
              }}>
                {action.icon}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                color: action.color,
                marginBottom: '4px'
              }}>
                {action.title}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                {action.desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 메인 콘텐츠 그리드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
        gap: '24px'
      }}>
        {/* 최근 대회 현황 */}
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: '0',
              color: '#1f2937'
            }}>
              📋 최근 대회 현황
            </h3>
            <button
              onClick={() => navigate('/tournaments')}
              style={{
                background: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              전체 보기
            </button>
          </div>

          {recentTournaments.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏆</div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                아직 개최한 대회가 없습니다
              </div>
              <div style={{ fontSize: '14px' }}>
                첫 번째 대회를 개최해보세요!
              </div>
              <button
                onClick={() => navigate('/association/create-tournament')}
                style={{
                  marginTop: '16px',
                  padding: '12px 24px',
                  background: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ➕ 대회 생성하기
              </button>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {recentTournaments.map(tournament => (
                <div
                  key={tournament.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/association/manage-tournament/${tournament.id}`)}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    marginRight: '16px'
                  }}>
                    🏆
                  </div>

                  <div style={{ flex: '1' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: '#1f2937'
                      }}>
                        {tournament.name}
                      </span>
                      {getStatusBadge(tournament.status)}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      display: 'flex',
                      gap: '12px'
                    }}>
                      <span>참가자: {tournament.participants?.length || 0}명</span>
                      <span>시작일: {formatDate(tournament.startDate)}</span>
                      <span>상금: {tournament.prizePool?.toLocaleString() || 0}원</span>
                    </div>
                  </div>

                  <div style={{
                    fontSize: '20px',
                    color: '#d1d5db'
                  }}>
                    →
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 대기 중인 참가 신청 */}
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#1f2937'
          }}>
            📝 대기 중인 참가 신청
          </h3>

          {pendingApplications.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                대기 중인 신청이 없습니다
              </div>
              <div style={{ fontSize: '14px' }}>
                새로운 참가 신청을 기다리고 있습니다.
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              {pendingApplications.map(application => (
                <div
                  key={application.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    background: '#fef3c7',
                    borderRadius: '12px',
                    border: '1px solid #fcd34d'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    marginRight: '16px'
                  }}>
                    👤
                  </div>

                  <div style={{ flex: '1' }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '16px',
                      color: '#1f2937',
                      marginBottom: '4px'
                    }}>
                      {application.userNickname}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      display: 'flex',
                      gap: '12px'
                    }}>
                      <span>MMR: {application.userMMR}</span>
                      <span>신청일: {formatDate(application.createdAt)}</span>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={() => handleApplicationDecision(application.id, 'approved')}
                      style={{
                        padding: '6px 12px',
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      승인
                    </button>
                    <button
                      onClick={() => handleApplicationDecision(application.id, 'rejected')}
                      style={{
                        padding: '6px 12px',
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      거절
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 최근 활동 */}
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          padding: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '24px',
            color: '#1f2937'
          }}>
            📈 최근 활동
          </h3>

          {recentActivities.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📈</div>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                최근 활동이 없습니다
              </div>
              <div style={{ fontSize: '14px' }}>
                대회를 개최하고 관리해보세요!
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              {recentActivities.map(activity => (
                <div
                  key={activity.id}
                  style={{
                    padding: '12px 16px',
                    background: '#f8fafc',
                    borderRadius: '8px',
                    borderLeft: '4px solid #667eea'
                  }}
                >
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '4px'
                  }}>
                    {activity.action}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280'
                  }}>
                    {formatDate(activity.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 대회 자동화 안내 */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          padding: '32px',
          color: '#fff',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}>
          <h4 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#fff'
          }}>
            🤖 대회 자동화 시스템
          </h4>
          
          <p style={{
            fontSize: '14px',
            margin: '0',
            marginBottom: '16px',
            opacity: '0.9',
            lineHeight: '1.5'
          }}>
            참가 신청부터 대진표 생성, 결과 처리까지 모든 과정을 자동화하여 
            대회 관리를 더욱 쉽고 효율적으로 만들어보세요!
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              opacity: '0.9'
            }}>
              <span>✅</span>
              <span>참가 신청 자동 접수</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              opacity: '0.9'
            }}>
              <span>✅</span>
              <span>MMR 기반 대진표 자동 생성</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              opacity: '0.9'
            }}>
              <span>✅</span>
              <span>경기 결과 실시간 업데이트</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              opacity: '0.9'
            }}>
              <span>✅</span>
              <span>우승자 자동 결정 및 MMR 반영</span>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/association/create-tournament')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              padding: '12px 20px',
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={e => {
              e.target.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseOut={e => {
              e.target.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            🚀 지금 첫 대회 시작하기
          </button>
        </div>
      </div>

      {/* 하단 도움말 */}
      <div style={{
        marginTop: '40px',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '16px',
        padding: '24px',
        border: '1px solid #bae6fd'
      }}>
        <h4 style={{
          fontSize: '18px',
          fontWeight: '700',
          marginBottom: '16px',
          color: '#0369a1'
        }}>
          💡 협회 운영 가이드
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c4a6e',
              marginBottom: '4px'
            }}>
              🎯 대회 기획
            </div>
            <div style={{
              fontSize: '12px',
              color: '#075985',
              lineHeight: '1.4'
            }}>
              목적에 맞는 대회 형식과 규칙을 설정하세요
            </div>
          </div>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c4a6e',
              marginBottom: '4px'
            }}>
              👥 참가자 관리
            </div>
            <div style={{
              fontSize: '12px',
              color: '#075985',
              lineHeight: '1.4'
            }}>
              신청자의 실력과 자격을 검토하여 승인하세요
            </div>
          </div>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c4a6e',
              marginBottom: '4px'
            }}>
              ⚙️ 자동화 활용
            </div>
            <div style={{
              fontSize: '12px',
              color: '#075985',
              lineHeight: '1.4'
            }}>
              시스템의 자동화 기능을 활용해 효율적으로 운영하세요
            </div>
          </div>
          <div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c4a6e',
              marginBottom: '4px'
            }}>
              📊 성과 분석
            </div>
            <div style={{
              fontSize: '12px',
              color: '#075985',
              lineHeight: '1.4'
            }}>
              대회 후 통계를 분석하여 다음 대회를 개선하세요
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssociationDashboard;